import React from 'react';
import { X, RotateCcw, Mail, Phone, Building2, MapPin, User as UserIcon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useServiceRequest, useCloneServiceRequest } from './serviceRequestHooks';

export default function ServiceRequestDetailModal({ publicId, onClose, onRequestAgain }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { data, isLoading } = useServiceRequest(publicId);
  const clone = useCloneServiceRequest();
  const sr = data?.service_request;

  const handleClone = async () => {
    const result = await clone.mutateAsync(publicId);
    onRequestAgain?.(result?.draft);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-medium font-rasa">Service request</h3>
            {sr && <p className="text-xs text-gray-500 mt-0.5">#{sr.public_id.slice(0, 8)}</p>}
          </div>
          <button onClick={onClose} className="p-1 rounded-md text-gray-500 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>

        {isLoading && <div className="p-6 text-sm text-gray-500">Loading…</div>}
        {sr && (
          <div className="p-5 space-y-5">
            <Section label="Services" isDark={isDark}>
              {sr.triage_state === 'pending' && (
                <div className="text-sm font-rasa text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/20 rounded-md px-3 py-2">
                  Waiting on triage — we'll route this to the right team shortly.
                </div>
              )}
              {sr.items?.length > 0 && (
                <ul className="space-y-1">
                  {sr.items.map((i) => (
                    <li key={i.slug} className="text-sm font-rasa flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      {i.label}
                      <span className={`text-xs ml-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        ({i.department_slug.replace('_', ' ')} / {i.service_area_slug.replace('_', ' ')})
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {sr.program_types?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {sr.program_types.map((p) => (
                    <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-rasa">
                      {p.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              )}
            </Section>

            <Section label="Contact" isDark={isDark}>
              <Row icon={UserIcon} text={sr.contact?.name} />
              <Row icon={Mail} text={sr.contact?.email} />
              {sr.contact?.phone && <Row icon={Phone} text={sr.contact.phone} />}
              {sr.company && <Row icon={Building2} text={sr.company} />}
              {formatAddress(sr) && <Row icon={MapPin} text={formatAddress(sr)} />}
            </Section>

            {sr.notes && (
              <Section label="Notes" isDark={isDark}>
                <p className="text-sm font-rasa whitespace-pre-wrap">{sr.notes}</p>
              </Section>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className={`px-3 py-1.5 text-sm font-rasa rounded-md ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleClone}
                disabled={clone.isPending}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-rasa rounded-md bg-blue-500 text-white hover:bg-blue-600"
              >
                <RotateCcw size={14} /> Request again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ label, children, isDark }) {
  return (
    <div>
      <div className={`text-xs uppercase tracking-wide font-rasa mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ icon: Icon, text }) {
  if (!text) return null;
  return (
    <div className="flex items-center gap-2 text-sm font-rasa">
      <Icon size={14} className="text-gray-400" />
      {text}
    </div>
  );
}

function formatAddress(sr) {
  if (!sr) return '';
  const line2 = [sr.city, sr.state].filter(Boolean).join(', ');
  return [sr.address, line2, sr.zip_code].filter(Boolean).join(' ').trim();
}
